/**
 * StudioSwift by WRG - Cart Drawer JavaScript
 * Handles: Cart modal open/close, remove items, update cart count
 */

(function () {
  'use strict';

  const CartDrawer = {
    selectors: {
      drawer: '.js-cart-drawer',
      openBtn: '.js-cart-drawer-open',
      closeBtn: '.js-cart-drawer-close',
      overlay: '.cart-drawer__overlay',
      items: '.js-cart-drawer-items',
      removeBtn: '.js-cart-item-remove',
      cartCount: '.js-cart-count',
      cartTotal: '.js-cart-total',
      hamburger: '.js-menu-open',
      emptyState: '.js-cart-drawer-empty'
    },

    classes: {
      open: 'is-open',
      bodyNoScroll: 'no-scroll',
      removing: 'is-removing'
    },

    init: function () {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) return;

      this.bindEvents();
    },

    bindEvents: function () {
      // Open cart drawer
      const openBtns = document.querySelectorAll(this.selectors.openBtn);
      openBtns.forEach(btn => {
        btn.addEventListener('click', this.open.bind(this));
      });

      // Close cart drawer
      const closeBtns = document.querySelectorAll(this.selectors.closeBtn);
      closeBtns.forEach(btn => {
        btn.addEventListener('click', this.close.bind(this));
      });

      // Remove items
      const removeBtns = this.drawer.querySelectorAll(this.selectors.removeBtn);
      removeBtns.forEach(btn => {
        btn.addEventListener('click', this.removeItem.bind(this));
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains(this.classes.open)) {
          this.close();
        }
      });

      // ─────────────────────────────────────────────────────────────────────
      // Close on outside click — uses CAPTURE phase + pointerdown so it fires
      // before any child handler (e.g. size buttons / style tabs) that calls
      // e.stopPropagation(). A bubbling-phase listener on `document` would
      // never receive the event once stopPropagation() is called by those
      // child handlers.
      // ─────────────────────────────────────────────────────────────────────
      document.addEventListener('pointerdown', (e) => {
        if (!this.drawer.classList.contains(this.classes.open)) return;

        const isClickInside = this.drawer.contains(e.target);
        const isCartButton = e.target.closest(this.selectors.openBtn);

        if (!isClickInside && !isCartButton) {
          this.close();
        }
      }, true); // true = capture phase
    },

    open: function (e) {
      if (e) e.preventDefault();

      const hamburger = document.querySelector(this.selectors.hamburger);
      if (hamburger) {
        hamburger.style.opacity = '0';
        hamburger.style.visibility = 'hidden';
        hamburger.style.pointerEvents = 'none';
      }

      this.drawer.classList.add(this.classes.open);
      this.drawer.setAttribute('aria-hidden', 'false');

      // Only prevent scroll on mobile (full-screen modal)
      if (window.innerWidth <= 990) {
        document.body.classList.add(this.classes.bodyNoScroll);

        // Focus first close button for accessibility (mobile only)
        const closeBtn = this.drawer.querySelector(this.selectors.closeBtn);
        if (closeBtn) {
          setTimeout(() => closeBtn.focus(), 100);
        }
      }

      // Refresh cart data
      this.refreshCart();
    },

    close: function () {
      const hamburger = document.querySelector(this.selectors.hamburger);
      if (hamburger) {
        hamburger.style.opacity = '1';
        hamburger.style.visibility = 'visible';
        hamburger.style.pointerEvents = 'auto';
      }

      this.drawer.classList.remove(this.classes.open);
      this.drawer.setAttribute('aria-hidden', 'true');

      // Remove body scroll lock (mobile only, but safe to call on desktop)
      document.body.classList.remove(this.classes.bodyNoScroll);
    },

    removeItem: function (e) {
      e.preventDefault();
      const btn = e.currentTarget;
      const itemKey = btn.getAttribute('data-item-key');
      const itemElement = btn.closest('.cart-drawer__item');

      if (!itemKey || !itemElement) return;

      // Add removing state
      itemElement.classList.add(this.classes.removing);

      // Remove item via Cart API
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      })
        .then(response => response.json())
        .then(cart => {
          // Remove item from DOM
          itemElement.remove();

          // Update cart count and total
          this.updateCartUI(cart);

          // Notify cart page so it can refresh without reload
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

          // Show empty state if no items
          if (cart.item_count === 0) {
            this.showEmptyState();
          }
        })
        .catch(error => {
          console.error('Error removing item:', error);
          itemElement.classList.remove(this.classes.removing);
          alert('Failed to remove item. Please try again.');
        });
    },

    refreshCart: function () {
      // Use Shopify Section Rendering API for better performance
      // The cart drawer is rendered within the header section
      const sectionId = document.querySelector('[data-section-id]')?.dataset?.sectionId;

      if (sectionId) {
        // Fetch only the header section with updated cart
        fetch(`${window.location.pathname}?section_id=${sectionId}`)
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newCartDrawer = doc.querySelector('.js-cart-drawer');

            if (newCartDrawer && this.drawer) {
              // Preserve open state
              const wasOpen = this.drawer.classList.contains(this.classes.open);

              // Replace drawer content
              this.drawer.innerHTML = newCartDrawer.innerHTML;

              // Restore open state if it was open
              if (wasOpen) {
                this.drawer.classList.add(this.classes.open);
                this.drawer.setAttribute('aria-hidden', 'false');
              }

              // Re-bind events for new elements
              this.rebindDrawerEvents();
            }
          })
          .catch(error => {
            console.error('Error refreshing cart section:', error);
            // Fallback: just update counts
            this.refreshCartData();
          });
      } else {
        // Fallback: just update cart counts if section ID not found
        this.refreshCartData();
      }
    },

    refreshCartData: function () {
      // Fetch current cart state (JSON only)
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.updateCartUI(cart);
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
        });
    },

    rebindDrawerEvents: function () {
      // Re-bind close buttons
      const closeBtns = this.drawer.querySelectorAll(this.selectors.closeBtn);
      closeBtns.forEach(btn => {
        btn.addEventListener('click', this.close.bind(this));
      });

      // Re-bind remove buttons
      const removeBtns = this.drawer.querySelectorAll(this.selectors.removeBtn);
      removeBtns.forEach(btn => {
        btn.addEventListener('click', this.removeItem.bind(this));
      });
    },

    updateCartUI: function (cart) {
      // Update cart count
      const countElements = document.querySelectorAll(this.selectors.cartCount);
      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update cart total (use store's money_with_currency format)
      const totalElements = document.querySelectorAll(this.selectors.cartTotal);
      totalElements.forEach(el => {
        el.textContent = this.formatMoney(cart.total_price);
      });
    },

    showEmptyState: function () {
      const itemsContainer = this.drawer.querySelector(this.selectors.items);
      const emptyState = this.drawer.querySelector(this.selectors.emptyState);
      const footer = this.drawer.querySelector('.cart-drawer__footer');

      if (itemsContainer) itemsContainer.style.display = 'none';
      if (emptyState) emptyState.style.display = 'flex';
      if (footer) footer.style.display = 'none';
    },

    hideEmptyState: function () {
      const itemsContainer = this.drawer.querySelector(this.selectors.items);
      const emptyState = this.drawer.querySelector(this.selectors.emptyState);
      const footer = this.drawer.querySelector('.cart-drawer__footer');

      if (itemsContainer) itemsContainer.style.display = 'flex';
      if (emptyState) emptyState.style.display = 'none';
      if (footer) footer.style.display = 'block';
    },

    formatMoney: function (cents) {
      const format = this.drawer?.dataset.moneyWithCurrencyFormat;
      const amount = (cents / 100).toFixed(2);
      if (format) {
        return format
          .replace(/\{\{amount\}\}/g, amount)
          .replace(/\{\{amount_no_decimals\}\}/g, Math.round(cents / 100).toString())
          .replace(/\{\{amount_with_comma_separator\}\}/g, amount.replace('.', ','))
          .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
      }
      return '$' + amount;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
  } else {
    CartDrawer.init();
  }

  // Re-initialize on Shopify section reload (Theme Editor)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.js-cart-drawer')) {
      CartDrawer.init();
    }
  });

  // Listen for cart updates from other parts of the app
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) {
      // Update cart counts and totals
      CartDrawer.updateCartUI(e.detail.cart);

      // Refresh drawer content to show new items
      CartDrawer.refreshCart();

      // Auto-open drawer when item is added
      if (!CartDrawer.drawer.classList.contains(CartDrawer.classes.open)) {
        setTimeout(() => {
          CartDrawer.open();
        }, 300);
      }
    }
  });

})();
